import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Descriptions, Skeleton, Space, Tabs, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, StopOutlined } from "@ant-design/icons";
import { App as AntdApp } from "antd";
import type { TenantDetailResponse, TenantResponse } from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { errorMessage } from "../lib/errors";
import { GaCancelledError } from "../lib/ga-interceptor";
import KeysTab from "./tenant-detail/KeysTab";
import WhitelistTab from "./tenant-detail/WhitelistTab";
import QuotaTab from "./tenant-detail/QuotaTab";
import SuppressionsTab from "./tenant-detail/SuppressionsTab";

const { Title } = Typography;

export default function TenantDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { modal } = AntdApp.useApp();

  const detailQuery = useQuery({
    queryKey: ["tenant", id],
    queryFn: () => apiRequest<TenantDetailResponse>(`/tenants/${id}`),
    enabled: Boolean(id),
  });

  const tenant = detailQuery.data?.tenant;

  const disableTenant = (): void => {
    modal.confirm({
      title: "Disable tenant?",
      content: "Suspends the tenant. Requires step-up verification.",
      okText: "Disable",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiRequest<TenantResponse>(`/tenants/${id}/disable`, { method: "POST" });
          message.success("Tenant disabled");
          void detailQuery.refetch();
        } catch (err) {
          if (err instanceof GaCancelledError) return;
          message.error(errorMessage(err, "Failed to disable tenant"));
        }
      },
    });
  };

  if (detailQuery.isLoading) {
    return (
      <Card>
        <Skeleton active />
      </Card>
    );
  }

  if (!tenant) {
    return (
      <Card>
        <Title level={4}>Tenant not found</Title>
        <Button onClick={() => navigate("/tenants")}>Back to tenants</Button>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate("/tenants")} style={{ paddingLeft: 0 }}>
              Tenants
            </Button>
            <Title level={4} style={{ marginTop: 8 }}>
              {tenant.name} <Tag color={tenant.status === "active" ? "green" : "red"}>{tenant.status}</Tag>
            </Title>
          </div>
          {tenant.status === "active" && (
            <Button danger icon={<StopOutlined />} onClick={disableTenant}>
              Disable tenant
            </Button>
          )}
        </div>
        <Descriptions size="small" column={2} style={{ marginTop: 12 }}>
          <Descriptions.Item label="Slug">{tenant.slug}</Descriptions.Item>
          <Descriptions.Item label="default_from">{tenant.default_from}</Descriptions.Item>
          <Descriptions.Item label="default_priority">{tenant.default_priority}</Descriptions.Item>
          <Descriptions.Item label="quota_per_min">{tenant.quota_per_min}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card>
        <Tabs
          defaultActiveKey="keys"
          items={[
            { key: "keys", label: "API Keys", children: <KeysTab tenantId={id} /> },
            { key: "whitelist", label: "From whitelist", children: <WhitelistTab tenantId={id} /> },
            { key: "quota", label: "Quota", children: <QuotaTab tenantId={id} /> },
            { key: "suppressions", label: "Suppressions", children: <SuppressionsTab tenantId={id} /> },
          ]}
        />
      </Card>
    </Space>
  );
}
